<template>
  <div class="bg-[#F2F6FA] min-h-screen">
    <div class="flex flex-col w-full pb-20 py-4">
      <section class="w-full">
        <!-- Page Header -->
        <div class="flex items-center justify-between md:mt-5 mb-2 md:mb-0 mt-2 px-4">
          <div class="flex justify-between items-center w-full">
            <div>
              <div class="items-center gap-2 d-flex">
                <v-icon
                  color="primary"
                  class="cursor-pointer"
                  size="28"
                >
                  mdi-file-document-check
                </v-icon>
                <div class="flex items-center gap-1">
                  <p class="text-base md:text-[22px] font-bold">
                    SEO Audit
                    <sup class="text-sm text-gray-500 pl-2">Beta</sup>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Page Description -->
        <div class="flex items-center justify-start mb-3 px-4">
          <div class="items-center gap-2 d-flex">
            <div class="flex items-center gap-1">
              <p class="text-lg md:text-[18px] text-gray-600">
                Analyze your business location's SEO performance and get actionable insights
              </p>
            </div>
          </div>
        </div>

        <!-- Main Content -->
        <div class="w-full min-h-[calc(100vh-200px)] flex items-center justify-center">
          <!-- Selection Form -->
          <div v-if="!auditData" class="w-full max-w-4xl p-8 bg-white rounded-lg shadow-md mx-auto form-container flex flex-col justify-center">
            <div class="text-center mb-8">
              <h2 class="text-2xl font-bold text-gray-800 mb-2">SEO Audit Report</h2>
              <p class="text-gray-600">Select your location to generate a comprehensive SEO analysis</p>
            </div>

            <!-- Select Your Location -->
            <div class="mb-12 relative">
              <label class="block text-gray-700 font-medium mb-3">Select Your Location</label>
              <div class="relative w-full flex-1">
                <div @click="toggleStoreDropdown($event)" class="relative" ref="locationContainer">
                  <v-text-field 
                    v-model="locationSearch" 
                    :placeholder="selectedStore ? `${selectedStore.gmbProfile?.title || selectedStore.name}` : 'Search and select location'"
                    hide-details 
                    dense 
                    outlined 
                    class="location-input"
                    :error="showValidation && !selectedStore"
                    color="rgba(0, 0, 0, 0.6)" 
                    background-color="white"
                    prepend-inner-icon="mdi-map-marker"
                    @click.stop="openDropdown"
                    @input="handleSearchInput"
                    @focus="openDropdown"
                  >
                    <template v-slot:append>
                      <v-icon>{{ showDropdown ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                    </template>
                  </v-text-field>

                  <div ref="showStoreDropdown" v-if="showDropdown"
                    class="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-2xl z-[9999] max-h-[400px] w-full overflow-y-auto">
                    <div class="p-2">
                      <div v-for="profile in filteredStores" :key="profile.wid"
                        class="platform p-4 rounded-md mb-2 border border-gray-100 relative" 
                        :class="{ 
                          'hover:bg-gray-100 cursor-pointer': profile.isSelectable,
                          'bg-gray-50 opacity-60 cursor-not-allowed': !profile.isSelectable,
                          'bg-blue-50 border border-blue-200': selectedWID === profile.wid && profile.isSelectable
                        }"
                        @click.stop="profile.isSelectable ? selectStore(profile) : null"
                      >
                        <div class="flex items-start gap-3">
                          <v-avatar size="40" color="grey lighten-3" class="mt-1">
                            <v-icon color="grey darken-2">mdi-store</v-icon>
                          </v-avatar>
                          <div class="flex flex-col w-full">
                            <span class="font-medium text-base text-gray-900">
                              {{ profile.gmbProfile?.title || profile.name }}
                            </span>
                            <div class="flex flex-wrap gap-1 mt-1" v-if="profile.gmbProfile?.category">
                              <span class="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">
                                {{ profile.gmbProfile.category }}
                              </span>
                            </div>
                            <div class="mt-2">
                              <div class="text-sm text-gray-600 flex items-start gap-1">
                                <v-icon small class="mt-0.5" color="gray">mdi-map-marker</v-icon>
                                <span>
                                  {{ profile.gmbProfile?.address || profile.sub_locality || profile.address || 'No address available' }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div v-if="!profile.isSelectable" class="absolute top-2 right-2 text-xs text-red-500 font-medium p-1 bg-red-100 rounded" title="This location cannot be selected because it is missing a Place ID.">
                          <v-icon small color="red">mdi-alert-circle-outline</v-icon>
                          Missing Place ID
                        </div>
                      </div>
                      <div v-if="filteredStores.length === 0" class="p-3 text-center text-gray-500">
                        No locations match your search
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Selected Location Details -->
              <div v-if="selectedStore" class="mt-4 p-4 border border-gray-200 rounded-md bg-gray-50">
                <div class="flex items-start gap-3">
                  <v-avatar size="40" color="primary lighten-4" class="mt-1">
                    <v-icon color="primary">mdi-store</v-icon>
                  </v-avatar>
                  <div class="flex-1">
                    <div class="font-medium text-lg text-gray-900">{{ selectedStore.gmbProfile?.title || selectedStore.name }}</div>
                    <div class="text-sm text-gray-600 mt-2">
                      <div class="flex items-start gap-2">
                        <v-icon small class="mt-0.5" color="gray">mdi-map-marker</v-icon>
                        <span>{{ selectedStore.gmbProfile?.address || selectedStore.sub_locality || selectedStore.address }}</span>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-2 mt-3">
                      <span v-if="selectedStore.gmbProfile?.category" class="text-xs px-2 py-1 bg-primary bg-opacity-10 rounded-full text-primary">
                        {{ selectedStore.gmbProfile.category }}
                      </span>
                    </div>
                  </div>
                  <v-btn icon small color="gray" @click.stop="clearSelectedStore" class="mt-1">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
            
            <!-- Generate Report Button -->
            <div class="flex justify-center mt-4">
              <v-btn
                color="primary"
                :disabled="!selectedStore"
                @click="generateAudit"
                :loading="isLoading"
                x-large
                class="px-8"
              >
                Perform SEO Audit
              </v-btn>
            </div>
          </div>

          <!-- Audit Report Content -->
          <div v-else class="w-full mt-6 report-content">
            <!-- Report Header with Actions -->
            <div class="bg-white shadow-sm rounded-lg p-6 mb-6 sticky top-0 z-10">
              <div class="flex justify-between items-center">
                <div class="flex items-center gap-4">
                  <h2 class="text-xl font-bold">SEO Audit Report</h2>
                  <p v-if="auditData.audit?.timestamp" class="text-sm text-gray-600">
                    Generated: {{ formatDate(auditData.audit.timestamp) }}
                  </p>
                </div>
                
                <div class="flex items-center gap-3">
                  <v-btn outlined color="primary" small @click="resetForm">
                    <v-icon left small>mdi-plus</v-icon>
                    New Audit
                  </v-btn>
                  
                  <v-btn color="primary" small @click="downloadReport">
                    <v-icon left small>mdi-download</v-icon>
                    Download
                  </v-btn>
                </div>
              </div>
            </div>

            <!-- Business Profile and Overall Score -->
            <div class="bg-white shadow-sm rounded-lg p-6 mb-6">
              <div class="flex items-start gap-6">
                <div class="flex-1">
                  <h3 class="text-xl font-bold text-gray-900">{{ auditData.audit.profile.title }}</h3>
                  <div class="text-sm text-gray-600 mt-2 flex items-start gap-2">
                    <v-icon small class="mt-0.5">mdi-map-marker</v-icon>
                    <span>{{ auditData.audit.profile.address }}</span>
                  </div>
                  <div class="flex flex-wrap gap-2 mt-3">
                    <span v-for="category in auditData.audit.profile.categories" :key="category"
                          class="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                      {{ category }}
                    </span>
                  </div>
                </div>
                <div class="text-center">
                  <div class="text-3xl font-bold text-primary">
                    SEO Score: {{ auditData.audit.profile.scores.overall.toFixed(1) }}
                  </div>
                  <div class="text-sm text-gray-500 mt-1">Overall Rating</div>
                </div>
              </div>
            </div>

            <!-- NEW: SEO Analysis Summary -->
            <div v-if="auditData?.audit" class="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 class="text-xl font-bold mb-4 text-gray-800">SEO Analysis Summary</h3>
              <p class="text-gray-700 whitespace-pre-line">{{ auditData.audit.ai_analysis }}</p>
            </div>

            <!-- NEW: SWOT Analysis -->
            <div v-if="auditData?.audit" class="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 class="text-xl font-bold mb-4 text-center text-gray-800">SWOT Analysis</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <!-- Strengths -->
                <div class="bg-green-50 p-4 rounded-lg shadow">
                  <h4 class="font-bold text-lg text-green-700 mb-3 flex items-center">
                    <v-icon color="green" class="mr-2">mdi-thumb-up-outline</v-icon> Strengths
                  </h4>
                  <ul v-if="auditData.audit.strengths && auditData.audit.strengths.length" class="list-disc list-inside space-y-1 pl-5 text-gray-700">
                    <li v-for="(item, index) in auditData.audit.strengths" :key="`swot-strength-${index}`">{{ item }}</li>
                  </ul>
                  <p v-else class="text-gray-500 italic">No specific strengths identified.</p>
                </div>
                <!-- Weaknesses -->
                <div class="bg-red-50 p-4 rounded-lg shadow">
                  <h4 class="font-bold text-lg text-red-700 mb-3 flex items-center">
                    <v-icon color="red" class="mr-2">mdi-thumb-down-outline</v-icon> Weaknesses
                  </h4>
                  <ul v-if="auditData.audit.weaknesses && auditData.audit.weaknesses.length" class="list-disc list-inside space-y-1 pl-5 text-gray-700">
                    <li v-for="(item, index) in auditData.audit.weaknesses" :key="`swot-weakness-${index}`">{{ item }}</li>
                  </ul>
                  <p v-else class="text-gray-500 italic">No specific weaknesses identified.</p>
                </div>
                <!-- Opportunities (from recommendations) -->
                <div class="bg-blue-50 p-4 rounded-lg shadow">
                  <h4 class="font-bold text-lg text-blue-700 mb-3 flex items-center">
                    <v-icon color="blue" class="mr-2">mdi-lightbulb-on-outline</v-icon> Opportunities
                  </h4>
                  <ul v-if="auditData.audit.recommendations && auditData.audit.recommendations.length" class="list-disc list-inside space-y-1 pl-5 text-gray-700">
                    <li v-for="(item, index) in auditData.audit.recommendations" :key="`swot-opportunity-${index}`">{{ item }}</li>
                  </ul>
                  <p v-else class="text-gray-500 italic">No specific opportunities identified.</p>
                </div>
                <!-- Threats -->
                <div class="bg-yellow-50 p-4 rounded-lg shadow">
                  <h4 class="font-bold text-lg text-yellow-700 mb-3 flex items-center">
                    <v-icon color="amber" class="mr-2">mdi-alert-outline</v-icon> Threats
                  </h4>
                  <ul v-if="auditData.audit.threats && auditData.audit.threats.length" class="list-disc list-inside space-y-1 pl-5 text-gray-700">
                    <li v-for="(item, index) in auditData.audit.threats" :key="`swot-threat-${index}`">{{ item }}</li>
                  </ul>
                  <p v-else class="text-gray-500 italic">No specific threats identified.</p>
                </div>
              </div>
            </div>

            <!-- Category Scores -->
            <div class="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 class="text-lg font-semibold mb-6">Category Performance</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div v-for="(score, category) in auditData.audit.profile.scores.categories" :key="category"
                     class="p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                     @click="scrollToDetailedMetric(category)">
                  <div class="flex items-center justify-between mb-2">
                    <h4 class="font-medium text-gray-700">{{ formatCategoryName(category) }}</h4>
                    <span class="text-xl font-bold" :class="getCategoryScoreColor(score)">
                      {{ score.toFixed(1) }}
                    </span>
                  </div>
                  <p class="text-sm text-gray-600">{{ auditData.audit.category_definitions[category] }}</p>
                  <v-progress-linear
                    :value="score * 10"
                    height="4"
                    rounded
                    class="mt-3"
                    :color="getCategoryScoreColor(score, true)"
                  ></v-progress-linear>
                </div>
              </div>
            </div>

            <!-- Detailed Metrics -->
            <div class="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 class="text-lg font-semibold mb-6">Detailed Metrics</h3>
              <div class="space-y-8">
                <div v-for="categoryKey in orderedDetailedMetricsCategories" :key="categoryKey" class="border-b pb-6 last:border-0">
                  <h4 :id="`detailed-metric-${categoryKey}`" class="text-lg font-medium mb-4 scroll-mt-20">{{ formatCategoryName(categoryKey) }}</h4>
                  <div class="space-y-6" v-if="groupedDatapointScores[categoryKey]">
                    <div v-for="(data, key) in groupedDatapointScores[categoryKey]" :key="key" class="bg-gray-50 rounded-lg p-4">
                      <div class="flex justify-between items-start mb-3">
                        <div class="flex-1">
                          <h5 class="font-medium text-gray-800">{{ formatMetricName(key) }}</h5>
                          <div class="text-sm text-gray-600 mt-1">Score: {{ data.score.toFixed(1) }}</div>
                        </div>
                      </div>
                      <div class="text-sm">
                        <div v-if="typeof data.value === 'string'" class="text-gray-700">
                          {{ data.value }}
                        </div>
                        <div v-else-if="Array.isArray(data.value)" class="space-y-1">
                          <div v-for="(item, index) in data.value" :key="index" class="text-gray-700">
                            {{ formatArrayValue(item) }}
                          </div>
                        </div>
                        <div v-else-if="typeof data.value === 'object'" class="space-y-2">
                          <pre class="text-sm text-gray-700 whitespace-pre-wrap">{{ formatObjectValue(data.value) }}</pre>
                        </div>
                      </div>
                      <div class="mt-3 text-sm text-gray-600 italic">{{ data.feedback }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/utils/firebase.utils';
import html2pdf from 'html2pdf.js';

export default {
  name: 'GmbSeoAudit',
  
  data() {
    return {
      locationSearch: "",
      selectedStore: null,
      stores: [],
      selectedWID: null,
      showDropdown: false,
      loading: true,
      isLoading: false,
      showValidation: false,
      auditData: null
    };
  },

  created() {
    // Check authentication state when component is created
    if (!this.$store.state._isAuth) {
      this.$router.push('/login');
      return;
    }
    // Initialize component data
    this.fetchStores();
  },

  computed: {
    filteredStores() {
      if (!this.stores) return [];
      if (!this.locationSearch) return this.stores;

      const searchTerm = this.locationSearch.toLowerCase().trim();
      return this.stores.filter(store => {
        const title = (store.gmbProfile?.title || store.name || '').toLowerCase();
        const address = (store.gmbProfile?.address || store.sub_locality || store.address || '').toLowerCase();
        const category = (store.gmbProfile?.category || '').toLowerCase();
        const city = (store.city || '').toLowerCase();

        return title.includes(searchTerm) || 
               address.includes(searchTerm) || 
               category.includes(searchTerm) || 
               city.includes(searchTerm);
      });
    },

    groupedDatapointScores() {
      if (!this.auditData?.audit?.datapoint_scores) return {};
      
      const grouped = {};
      Object.entries(this.auditData.audit.datapoint_scores).forEach(([key, value]) => {
        const category = key.split('_')[0].toLowerCase().trim();
        if (!grouped[category]) {
          grouped[category] = {};
        }
        grouped[category][key] = value;
      });
      
      return grouped;
    },

    orderedDetailedMetricsCategories() {
      if (!this.auditData?.audit?.profile?.scores?.categories) return [];
      return Object.keys(this.auditData.audit.profile.scores.categories).map(k => k.toLowerCase().trim());
    }
  },

  methods: {
    async fetchStores() {
      this.loading = true;
      try {
        const storeRef = collection(db, "users", this.currentUID, "fly_workspaces");
        const storeQuery = query(
          storeRef,
          where("admin", "==", this.WID),
          where("storeWorkspace", "==", true)
        );
        const querySnapshot = await getDocs(storeQuery);
        this.stores = querySnapshot.docs.map((docSnapshot) => {
          const data = docSnapshot.data();
          const placeId = data.gmbProfile?.place_id || 
                        data.place_id || 
                        data.gmb_shallow_profile_data?.place_id || 
                        data.gmb_profile_data?.place_id ||
                        data.gmbProfile?.placeId ||
                        null;
          
          return {
            ...data,
            wid: docSnapshot.id,
            placeId: placeId,
            isSelectable: !!placeId
          };
        });
      } catch (error) {
        console.error("Error fetching stores:", error);
        this.$snackbar.show({text: "Error fetching your locations.", color: "error"});
      } finally {
        this.loading = false;
      }
    },

    openDropdown() {
      this.showDropdown = true;
      if (this.selectedStore) {
        this.locationSearch = '';
      }
    },

    handleSearchInput() {
      this.showDropdown = true;
    },

    selectStore(store) {
      if (!store.isSelectable) {
        this.$snackbar.show({text: "This location cannot be selected because it is missing a Place ID.", color: "warning"});
        return;
      }
      this.selectedStore = { ...store };
      this.showDropdown = false;
      this.selectedWID = store.wid;
      this.locationSearch = store.gmbProfile?.title || store.name || '';
    },

    clearSelectedStore(event) {
      if (event) {
        event.stopPropagation();
      }
      this.selectedStore = null;
      this.selectedWID = null;
      this.locationSearch = '';
      this.showDropdown = false;
    },

    handleClickOutside(event) {
      const locationContainer = this.$refs.locationContainer;
      if (!locationContainer) return;
      
      if (!locationContainer.contains(event.target)) {
        this.showDropdown = false;
        if (this.selectedStore) {
          this.locationSearch = this.selectedStore.gmbProfile?.title || this.selectedStore.name || '';
        }
      }
    },

    async generateAudit() {
      this.showValidation = true;
      if (!this.selectedStore) {
        this.$snackbar.show({text: "Please select a location.", color: "error"});
        return;
      }

      this.isLoading = true;
      try {
        const storeQuery = encodeURIComponent(
          `${this.selectedStore.gmbProfile?.title || this.selectedStore.name || ''} ${this.selectedStore.sub_locality || this.selectedStore.address || ''}`
        ).trim();
        const url = `https://www.google.com/maps/search/?api=1&query=${storeQuery}&query_place_id=${this.selectedStore.placeId}`;

        const response = await fetch('http://localhost:8888/seo-audit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ url })
        });

        if (!response.ok) {
          throw new Error(`Failed to generate audit: ${response.statusText}`);
        }

        this.auditData = await response.json();
      } catch (error) {
        console.error('Error generating audit:', error);
        this.$snackbar.show({
          text: error.message || 'Failed to generate audit report',
          color: 'error'
        });
      } finally {
        this.isLoading = false;
        this.showValidation = false;
      }
    },

    resetForm() {
      this.selectedStore = null;
      this.selectedWID = null;
      this.locationSearch = '';
      this.showDropdown = false;
      this.auditData = null;
    },

    formatDate(timestamp) {
      return new Date(timestamp).toLocaleString();
    },

    formatCategoryName(category) {
      return category
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },

    formatMetricName(key) {
      return key
        .split('_')
        .slice(1)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },

    getCategoryScoreColor(score, isClass = false) {
      if (score >= 8) return isClass ? 'success' : 'text-green-600';
      if (score >= 6) return isClass ? 'warning' : 'text-orange-600';
      return isClass ? 'error' : 'text-red-600';
    },

    formatArrayValue(item) {
      if (typeof item === 'object') {
        return Object.entries(item)
          .map(([key, value]) => `${key}: ${value}`)
          .join(', ');
      }
      return item;
    },

    formatObjectValue(obj) {
      if (!obj) return 'N/A';
      return JSON.stringify(obj, null, 2);
    },

    scrollToDetailedMetric(categoryKey) {
      const elementId = `detailed-metric-${categoryKey}`;
      const element = this.$el.querySelector(`#${elementId}`); // Use this.$el.querySelector for better scoping in Vue components
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },

    async downloadReport() {
      if (!this.auditData) {
        this.$snackbar.show({text: "No report data to download.", color: "warning"});
        return;
      }

      const element = this.$el.querySelector('.report-content');
      if (element) {
        const options = {
          margin: 0.5,
          filename: `SEO_Audit_${this.auditData.audit.profile.title}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
        };
        html2pdf().from(element).set(options).save();
      }
    }
  },

  mounted() {
    this.fetchStores();
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>

<style scoped>
.location-input {
  width: 100% !important;
}

.location-input :deep(.v-input__slot) {
  background: white !important;
}

[ref="showStoreDropdown"] {
  position: absolute;
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.platform {
  transition: all 0.2s ease;
}

.platform:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.scroll-mt-20 { /* Tailwind class for scroll-margin-top: 5rem; (20 * 0.25rem) */
  scroll-margin-top: 5rem; 
}
</style> 